/* Star Maps UAE — Add-to-cart feedback for Teeinblue products (Prestige 10.x)
 * Fixes: (1) silent validation failure when a required personalisation field is empty,
 *        (2) no loading state while Teeinblue uploads the design before /cart/add.
 * Progressive enhancement: does nothing unless Teeinblue is active on the page.
 * Uses event delegation so it survives Teeinblue's late load and product re-renders.
 */
(function () {
  'use strict';

  /* ---- Copy: edit wording here ---- */
  var LABEL_SAVING        = 'Saving your design…';
  var TOAST_GENERIC       = 'Please complete the highlighted field ↑';
  var TOAST_LOCATION      = 'Please choose a location for your map ↑';
  var TOAST_ERROR         = 'Something went wrong — please try again';
  var LABEL_UPLOADING     = 'Uploading your photo…';   // shown on the button while a photo upload finishes
  /* --------------------------------- */

  var CHECK_DELAY_MS   = 150;   // wait for Teeinblue validation to render
  var SAFETY_TIMEOUT   = 15000; // release busy state no matter what
  var RETAP_GUARD_MS   = 1500;  // ignore reflex re-taps after release
  var TOAST_MS         = 3500;
  var SILENT_FAIL_MS   = 1200;  // no upload started by then = validation silently failed
  var UPLOAD_RETRY_MS  = 1500;  // re-try the tap while a chosen photo is still uploading
  var UPLOAD_MAX_MS    = 20000; // give up waiting for a photo upload after this
  var SELECTOR         = '.buy-buttons button[type="submit"]';

  var state = { busy: false, releasedAt: 0, timer: null, buttons: [], originalHTML: [], uploadStarted: false, clickAt: 0, lastCartEventAt: 0, photoUploads: 0, pendingButton: null, uploadWaitSince: 0, uploadTimer: null };
  var toast = null, toastTimer = null;

  /* Flag when Teeinblue starts uploading the design (fetch or XHR). */
  function watchNetwork() {
    var isUpload = function (u) { return /platform-customizations|\/cart\/add/.test(String(u)); };
    var isPhoto  = function (u) { return /wasabisys\.com|\/upload-url|\/uploaded-at/.test(String(u)); };
    var photoDone = function () {
      state.photoUploads = Math.max(0, state.photoUploads - 1);
      if (state.photoUploads === 0 && state.pendingButton) {
        var b = state.pendingButton; state.pendingButton = null;
        setTimeout(function () { continueSubmit(b); }, 250); // let Teeinblue register the upload first
      }
    };
    if (window.fetch) {
      var f = window.fetch;
      window.fetch = function (u) {
        if (isUpload(u)) state.uploadStarted = true;
        var p = f.apply(this, arguments);
        if (isPhoto(u)) { state.photoUploads++; p.then(photoDone, photoDone); }
        return p;
      };
    }
    if (window.XMLHttpRequest) {
      var o = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (m, u) {
        if (isUpload(u)) state.uploadStarted = true;
        if (isPhoto(u)) { state.photoUploads++; this.addEventListener('loadend', photoDone); }
        return o.apply(this, arguments);
      };
    }
  }
  watchNetwork();

  function isTeeinblue() { return document.body && document.body.classList.contains('teeinblue-enabled'); }

  function selector() {
    return (window.teeinblue && window.teeinblue.atcSubmitSelector) || SELECTOR;
  }

  function isVisible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  function tag(value) {
    try { if (window.clarity) window.clarity('set', 'atc_result', value); } catch (e) {}
  }

  /* ---------- toast ---------- */
  function showToast(msg) {
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'tib-atc-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, TOAST_MS);
  }

  /* ---------- busy state ---------- */
  function setBusy(on, label) {
    state.busy = on;
    if (on) {
      state.buttons = Array.prototype.slice.call(document.querySelectorAll(selector()));
      state.originalHTML = state.buttons.map(function (b) { return b.innerHTML; });
      /* No `disabled` and no `aria-busy` here: Teeinblue hands off to the theme by
         programmatically clicking this button after the upload, and Prestige's
         ProductForm ignores a submitter with aria-busy="true". Taps are blocked by
         the trusted-click guard below and pointer-events:none in CSS. */
      state.buttons.forEach(function (b) {
        b.setAttribute('data-tib-busy', 'true');
        b.innerHTML = '<span class="tib-spinner" aria-hidden="true"></span>' + (label || LABEL_SAVING);
      });
      clearTimeout(state.timer);
      state.timer = setTimeout(function () { setBusy(false); }, SAFETY_TIMEOUT);
    } else {
      state.buttons.forEach(function (b, i) {
        if (!b.isConnected) return;
        b.removeAttribute('data-tib-busy');
        b.innerHTML = state.originalHTML[i];
      });
      state.buttons = []; state.originalHTML = [];
      state.releasedAt = Date.now();
      clearTimeout(state.timer);
    }
  }

  function release() { state.lastCartEventAt = Date.now(); if (state.busy) setBusy(false); }

  /* ---------- validation failure ---------- */
  /* A photo field that already shows a chosen picture but is flagged invalid = its upload
     (done in a Teeinblue Web Worker, invisible to page-level hooks) hasn't landed yet. */
  function isPhotoStillUploading(field) {
    if (!field || !field.classList.contains('tee-field--photo')) return false;
    return !!(field.querySelector('img') || /another image/i.test(field.textContent || ''));
  }

  function findValidationError() {
    var emptyLoc = Array.prototype.filter.call(document.querySelectorAll('.tee-maps__input--empty'), isVisible)[0];
    if (emptyLoc) return { field: emptyLoc.closest('.tee-field') || emptyLoc.parentElement, message: TOAST_LOCATION };
    var required = Array.prototype.filter.call(document.querySelectorAll('.tee-required-message'), isVisible);
    var generic  = Array.prototype.filter.call(document.querySelectorAll('.tee-error__message'), isVisible);
    if (!required.length && !generic.length) return null;
    var field = required.length ? required[0].closest('.tee-field') : null;
    var isLocation = !!(field && (field.classList.contains('tee-field--star_maps') || field.querySelector('.tee-maps__input')));
    return { field: field, message: isLocation ? TOAST_LOCATION : TOAST_GENERIC };
  }

  function handleValidationError(err, button) {
    showToast(err.message);
    var form = button.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (form) form.classList.add('tib-touched');
    button.classList.remove('tib-shake');
    void button.offsetWidth; // restart animation
    button.classList.add('tib-shake');
    if (err.field) {
      err.field.scrollIntoView({ behavior: 'auto', block: 'center' });
      var input = err.field.querySelector('input, textarea, select');
      if (input) setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (e) {} }, 350);
    }
    tag('validation_error');
  }

  /* ---------- click handling (capture phase: runs before Teeinblue) ---------- */
  document.addEventListener('click', function (e) {
    if (!isTeeinblue()) return;
    var button = e.target.closest ? e.target.closest(selector()) : null;
    if (!button) return;
    if (e.isTrusted === false) return; // Teeinblue's programmatic hand-off click: let it through
    if (state.busy || Date.now() - state.releasedAt < RETAP_GUARD_MS) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (state.photoUploads > 0) { // a photo is still uploading: hold the tap, continue when done
      e.preventDefault();
      e.stopImmediatePropagation();
      // Teeinblue flags the photo field as "required" until its upload lands, so only the
      // location check is meaningful here; the full check runs when the tap is continued.
      var emptyLoc0 = Array.prototype.filter.call(document.querySelectorAll('.tee-maps__input--empty'), isVisible)[0];
      if (emptyLoc0) { handleValidationError({ field: emptyLoc0.closest('.tee-field') || emptyLoc0.parentElement, message: TOAST_LOCATION }, button); return; }
      state.pendingButton = button;
      setBusy(true, LABEL_UPLOADING);
      tag('photo_uploading');
      return;
    }
    trackSubmit(button);
  }, true);

  /* After a click reaches Teeinblue: decide validation-fail vs real submit. */
  function trackSubmit(button) {
    state.uploadStarted = false;
    state.clickAt = Date.now();
    setTimeout(function () {
      var err = findValidationError();
      if (err && isPhotoStillUploading(err.field)) { waitForPhotoUpload(button); return; }
      if (err) { if (state.busy) setBusy(false); handleValidationError(err, button); return; }
      if (state.lastCartEventAt >= state.clickAt) { if (state.busy) setBusy(false); return; } // already added
      state.uploadWaitSince = 0;
      setBusy(true); // (re)label to "Saving…"
      tag('submitted');
      setTimeout(function () {
        if (state.busy && !state.uploadStarted) {
          var late = findValidationError();
          if (late && isPhotoStillUploading(late.field)) { waitForPhotoUpload(button); return; }
          setBusy(false);
          state.releasedAt = 0;
          handleValidationError(late || { field: null, message: TOAST_GENERIC }, button);
        }
      }, SILENT_FAIL_MS);
    }, CHECK_DELAY_MS);
  }

  /* Hold the button in "Uploading your photo…" and re-issue the tap every UPLOAD_RETRY_MS
     until Teeinblue accepts it (upload landed) or UPLOAD_MAX_MS passes. */
  function waitForPhotoUpload(button) {
    if (!state.uploadWaitSince) { state.uploadWaitSince = Date.now(); tag('photo_uploading'); }
    setBusy(true, LABEL_UPLOADING);
    clearTimeout(state.timer); // no 15 s safety release while we are deliberately waiting
    clearTimeout(state.uploadTimer);
    if (Date.now() - state.uploadWaitSince > UPLOAD_MAX_MS) {
      state.uploadWaitSince = 0; setBusy(false); state.releasedAt = 0;
      handleValidationError({ field: null, message: TOAST_GENERIC }, button);
      return;
    }
    state.uploadTimer = setTimeout(function () {
      if (!button.isConnected) { state.uploadWaitSince = 0; setBusy(false); return; }
      state.busy = false;        // let the synthetic click through our own guard
      button.click();            // Teeinblue re-validates; succeeds once the upload has landed
      trackSubmit(button);       // will call waitForPhotoUpload again if still uploading
    }, UPLOAD_RETRY_MS);
  }

  /* Re-issue the held tap as a programmatic click (isTrusted=false, so our guard lets it
     through to Teeinblue), then track it like a normal submit. */
  function continueSubmit(button) {
    if (!button || !button.isConnected) { setBusy(false); return; }
    state.busy = false;              // allow the synthetic click path; label is refreshed in trackSubmit
    button.click();
    trackSubmit(button);
  }

  /* ---------- release triggers ---------- */
  ['cart:change', 'variant:add', 'cart:refresh', 'cart-drawer:refreshed'].forEach(function (ev) {
    document.addEventListener(ev, release);
  });
  document.addEventListener('cart:error', function () {
    if (state.busy) { setBusy(false); showToast(TOAST_ERROR); tag('cart_error'); }
  });

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) release();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    return true;
  }
  if (!watchDrawer()) {
    document.addEventListener('DOMContentLoaded', watchDrawer);
    window.addEventListener('load', watchDrawer);
  }
})();
